import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
interface StatItem {
  label: string;
  value: number;
  suffix: string;
  description: string;
}
const stats: StatItem[] = [{
  label: "01",
  value: 94.2,
  suffix: "%",
  description: "Maximum thermal reclamation from C&I exhaust streams using autonomic AI intelligence."
}, {
  label: "02",
  value: 1.12,
  suffix: "x",
  description: "Achieving industry-leading Power Usage Effectiveness through dynamic thermal adjustments."
}, {
  label: "03",
  value: 48.6,
  suffix: "%",
  description: "Direct reduction in Scope 2 emissions via autonomic thermal load balancing and reclamation."
}, {
  label: "04",
  value: 250,
  suffix: "ms",
  description: "Real-time AI processing of grid frequency fluctuations for immediate demand response."
}];
function AnimatedStat({
  item,
  index
}: {
  item: StatItem;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const intervalTime = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progressRatio = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progressRatio, 3);
      const nextCount = item.value * easeOut;
      const nextProgress = item.value * easeOut;
      setCount(nextCount);
      setProgress(nextProgress);
      if (currentStep >= steps) {
        setCount(item.value);
        setProgress(item.value);
        clearInterval(timer);
      }
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isVisible, item.value]);
  return <div data-section-id="26934" ref={ref} className="h-full" data-index={index}>
      <Card className="h-full rounded-none border-border bg-card/40 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 group relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-primary/5 to-transparent h-1/2 w-full -translate-y-full group-hover:animate-[scanline_5s_linear_infinite]" />
        <CardHeader className="pb-2">
          <CardTitle className="text-muted-foreground font-mono text-[10px] uppercase tracking-[0.2em]">
            {item.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-baseline gap-1">
            <span className="font-mono text-5xl font-bold tracking-tighter text-primary md:text-6xl">
              {count.toFixed(1)}
            </span>
            <span className="font-mono text-2xl text-primary/70 md:text-3xl">
              {item.suffix}
            </span>
          </div>
          
          <div className="space-y-2">
            <Progress value={progress} className="h-0.5 bg-muted/20 rounded-none" />
            <p className="text-muted-foreground font-sans text-xs leading-relaxed opacity-80">
              {item.description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function QuantifiableImpact() {
  return <section id="exergy-metrics" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.05]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-[0.05em] sm:text-4xl md:text-5xl mb-6 uppercase text-foreground">
            Precision <span className="text-primary">Intelligence</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-sans max-w-xl">
            Our autonomic thermal models eliminate energy waste through surgical precision. 
            We transform lost heat into reclaimed power for high-density facilities.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => <AnimatedStat key={i} item={stat} index={i} />)}
        </div>
      </div>
    </section>;
}